import React, { useEffect, useMemo, useState } from 'react';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Checkbox, Typography } from '@mui/material';
import * as subMatFolderActions from '../../store/subMatFolder';
import SubMatFolderIcon from '../G04docuSubMatFolder/SubMatFolderIcon';

const collectDescendantIds = (folderId, folders) => {
  const kids = folders.filter((f) => f.parentId === folderId);
  return kids.flatMap((k) => [k.id, ...collectDescendantIds(k.id, folders)]);
};

const collectAncestorIds = (folderId, folders) => {
  const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
  const ids = [];
  let cur = byId[folderId];
  while (cur?.parentId) {
    ids.push(cur.parentId);
    cur = byId[cur.parentId];
  }
  return ids;
};

const childCheckState = (folderId, folders, selectedSet) => {
  const descendants = collectDescendantIds(folderId, folders);
  if (!descendants.length) return { checked: selectedSet.has(folderId), indeterminate: false };
  const selectedCount = descendants.filter((id) => selectedSet.has(id)).length;
  const self = selectedSet.has(folderId);
  if (self && selectedCount === descendants.length) {
    return { checked: true, indeterminate: false };
  }
  if (!self && selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  return { checked: self && selectedCount === descendants.length, indeterminate: true };
};

const renderNodes = (nodes, folders, selectedSet, onToggle) => nodes.map((node) => {
  const children = subMatFolderActions.buildFolderTree(folders, node.id);
  const { checked, indeterminate } = childCheckState(node.id, folders, selectedSet);

  return (
    <TreeItem
      key={node.id}
      nodeId={node.id}
      label={(
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="small"
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => onToggle(node.id)}
            onClick={(e) => e.stopPropagation()}
            sx={{ p: 0.25 }}
          />
          <SubMatFolderIcon folder={node} />
          <Typography variant="body2" component="span" noWrap>
            {node.name}
          </Typography>
        </Box>
      )}
    >
      {children.length > 0 ? renderNodes(children, folders, selectedSet, onToggle) : null}
    </TreeItem>
  );
});

const SubMatTemplateFolderPicker = ({ folders, selectedIds, onChange, bare = false }) => {
  const [expanded, setExpanded] = useState([]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const roots = useMemo(
    () => subMatFolderActions.buildFolderTree(folders, null),
    [folders],
  );

  const rootIds = useMemo(() => roots.map((r) => r.id), [roots]);

  // 최상위(루트)만 펼쳐 바로 아래 단계까지 보이게
  useEffect(() => {
    if (!rootIds.length) return;
    setExpanded((prev) => (prev.length === 0 ? rootIds : prev));
  }, [rootIds]);

  const handleToggle = (folderId) => {
    const descendants = collectDescendantIds(folderId, folders);
    const bundle = [folderId, ...descendants];
    const isChecked = selectedIds.includes(folderId)
      && descendants.every((id) => selectedIds.includes(id));

    if (isChecked || (selectedIds.includes(folderId) && descendants.length === 0)) {
      // 자신 + 하위 해제 (조상은 유지 — 부분 선택 가능)
      const remove = new Set(bundle);
      onChange?.(selectedIds.filter((id) => !remove.has(id)));
      return;
    }

    // 자신 + 하위 선택. 조상도 경로용으로 체크
    const ancestors = collectAncestorIds(folderId, folders);
    onChange?.([...new Set([...selectedIds, ...bundle, ...ancestors])]);
  };

  return (
    <Box
      sx={{
        overflow: 'auto',
        p: 0.5,
        height: bare ? '100%' : undefined,
        maxHeight: bare ? 'none' : 280,
        ...(bare
          ? {}
          : { border: 1, borderColor: 'divider', borderRadius: 1 }),
      }}
    >
      <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          expanded={expanded}
          onNodeToggle={(_e, ids) => setExpanded(ids)}
          multiSelect={false}
          selected=""
          sx={{
            '& .MuiTreeItem-content': {
              py: 0.25,
              minHeight: 32,
            },
            '& .MuiTreeItem-label': {
              p: 0,
              pl: '4px',
              fontSize: '0.875rem',
            },
          }}
        >
          {renderNodes(roots, folders, selectedSet, handleToggle)}
        </TreeView>
    </Box>
  );
};

export default SubMatTemplateFolderPicker;
